import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, MoreVertical, Eye, EyeOff, Trash2, Home, Building2, Pencil, MessageSquare, RotateCcw, CheckCircle, XCircle, ChevronDown, ChevronUp, Send, Sparkles, AlertCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'sonner';
import { BoostListingButton } from '@/components/dashboard/BoostListingButton';

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  draft: 'bg-gray-100 text-gray-700',
  sold: 'bg-blue-100 text-blue-800',
  rented: 'bg-purple-100 text-purple-800',
};

const historyActionConfig: Record<string, { label: string; dot: string; icon: any }> = {
  feedback_sent: { label: 'QC sent feedback', dot: 'bg-amber-500', icon: MessageSquare },
  resubmitted: { label: 'You resubmitted', dot: 'bg-blue-500', icon: RotateCcw },
  approved: { label: 'Approved by QC', dot: 'bg-green-500', icon: CheckCircle },
  rejected: { label: 'Rejected by QC', dot: 'bg-red-500', icon: XCircle },
};

export function MyListings() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [filterTab, setFilterTab] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [expandedHistory, setExpandedHistory] = useState<Record<string, boolean>>({});
  const scope = searchParams.get('scope') === 'agency' ? 'agency' : 'mine';
  const isAgent = hasRole('agent') || hasRole('agency_admin');

  const { data: properties, isLoading } = useQuery({
    queryKey: ['my-listings', user?.id, scope],
    queryFn: async () => {
      let userIds: string[] = [user!.id];
      if (scope === 'agency' && isAgent) {
        const { data: myProfile } = await supabase
          .from('agent_profiles').select('agency_id').eq('user_id', user!.id).maybeSingle();
        const agencyId = myProfile?.agency_id
          ?? (await supabase.from('agencies').select('id').eq('created_by', user!.id).maybeSingle()).data?.id;
        if (agencyId) {
          const { data: team } = await supabase
            .from('agent_profiles').select('user_id').eq('agency_id', agencyId);
          const teamIds = (team || []).map(t => t.user_id);
          const { data: agency } = await supabase
            .from('agencies').select('created_by').eq('id', agencyId).maybeSingle();
          if (agency?.created_by) teamIds.push(agency.created_by);
          userIds = Array.from(new Set(teamIds));
        }
      }
      const { data, error } = await supabase
        .from('properties')
        .select('*, property_images(*)')
        .in('user_id', userIds)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Review history for all listed properties
  const propertyIds = (properties || []).map((p: any) => p.id);
  const { data: allHistory } = useQuery({
    queryKey: ['my-listings-review-history', propertyIds.join(',')],
    queryFn: async () => {
      if (propertyIds.length === 0) return [];
      const { data, error } = await supabase
        .from('property_review_history' as any)
        .select('*')
        .in('property_id', propertyIds)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: propertyIds.length > 0,
  });

  const historyByProperty: Record<string, any[]> = {};
  (allHistory || []).forEach((h: any) => {
    (historyByProperty[h.property_id] ||= []).push(h);
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('properties').update({ status: status as any }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
      toast.success(vars.status === 'active' ? 'Property is now visible on the site' : `Property set to ${vars.status}`);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Mark Sold / Rented: archives all open leads + notifies inquirers + hides from search
  const markSoldMutation = useMutation({
    mutationFn: async ({ property, finalStatus }: { property: any; finalStatus: 'sold' | 'rented' }) => {
      // 1. Update property
      const { error: pErr } = await supabase
        .from('properties')
        .update({ status: finalStatus as any })
        .eq('id', property.id);
      if (pErr) throw pErr;

      // 2. Find all open leads on this property
      const { data: openLeads } = await supabase
        .from('leads')
        .select('id, sender_id, name')
        .eq('property_id', property.id)
        .in('status', ['new', 'contacted']);

      if (openLeads && openLeads.length > 0) {
        const ids = openLeads.map((l: any) => l.id);
        // 3. Close them
        await supabase.from('leads').update({ status: 'closed' as any }).in('id', ids);

        // 4. Notify inquirers who were registered users
        const propTitle = getName(property.title);
        const notifications = openLeads
          .filter((l: any) => l.sender_id)
          .map((l: any) => ({
            user_id: l.sender_id,
            type: 'property_unavailable',
            title: `Property no longer available: ${propTitle}`,
            message: `The property "${propTitle}" you inquired about has just been ${finalStatus === 'sold' ? 'sold' : 'rented'}. We're sorry — your inquiry has been closed. Browse similar listings on our site.`,
            metadata: { property_id: property.id, action: 'property_unavailable', final_status: finalStatus },
          }));
        if (notifications.length > 0) {
          await supabase.from('notifications').insert(notifications);
        }
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
      toast.success(`Property marked as ${vars.finalStatus}. Open inquiries were closed and inquirers notified.`);
    },
    onError: (err: any) => toast.error(err.message),
  });


  const resubmitMutation = useMutation({
    mutationFn: async (property: any) => {
      if (!user) throw new Error('Not authenticated');
      const { error: updErr } = await supabase
        .from('properties')
        .update({ approval_status: 'pending_review' })
        .eq('id', property.id);
      if (updErr) throw updErr;

      await supabase.from('property_review_history' as any).insert({
        property_id: property.id,
        reviewer_id: user.id,
        action: 'resubmitted',
        notes: null,
      });

      const propHistory = historyByProperty[property.id] || [];
      const lastReviewerEntry = propHistory.find(
        (h: any) => h.action === 'feedback_sent' || h.action === 'rejected' || h.action === 'approved'
      );
      const propTitle = getName(property.title);
      if (lastReviewerEntry?.reviewer_id) {
        await supabase.from('notifications').insert({
          user_id: lastReviewerEntry.reviewer_id,
          type: 'property_resubmitted',
          title: `Property resubmitted: ${propTitle}`,
          message: `The owner has addressed your feedback and resubmitted "${propTitle}" for review.`,
          metadata: { property_id: property.id, action: 'resubmitted' },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
      queryClient.invalidateQueries({ queryKey: ['my-listings-review-history'] });
      toast.success('Property resubmitted for review.');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('properties').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
      toast.success('Property deleted');
      setDeleteTarget(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const getName = (val: any) => typeof val === 'object' ? (val?.en || '') : (val || '');

  const filtered = properties?.filter((p: any) => {
    if (filterTab === 'all') return true;
    return p.status === filterTab;
  }) || [];

  const counts = {
    all: properties?.length || 0,
    active: properties?.filter((p: any) => p.status === 'active').length || 0,
    draft: properties?.filter((p: any) => p.status === 'draft').length || 0,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            {scope === 'agency' ? <><Building2 className="h-5 w-5" /> Agency Listings</> : 'My Listings'}
          </h2>
          {scope === 'agency' && (
            <p className="text-xs text-muted-foreground mt-0.5">All properties listed by agents in your agency</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAgent && scope === 'mine' && (
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/listings?scope=agency')}>
              <Building2 className="h-4 w-4 mr-1" />Agency View
            </Button>
          )}
          {isAgent && scope === 'agency' && (
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/listings')}>
              My Listings Only
            </Button>
          )}
          <Button size="sm" onClick={() => navigate('/dashboard/properties/new')}>
            <Plus className="h-4 w-4 mr-1" />Add Property
          </Button>
        </div>
      </div>

      <Tabs value={filterTab} onValueChange={setFilterTab}>
        <TabsList>
          <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="active">Active ({counts.active})</TabsTrigger>
          <TabsTrigger value="draft">Draft ({counts.draft})</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((p: any) => {
            const title = getName(p.title);
            const img = p.property_images?.find((i: any) => i.is_primary)?.url || p.property_images?.[0]?.url;
            const isApproved = p.approval_status === 'approved';
            const propHistory = historyByProperty[p.id] || [];
            const lastFeedback = propHistory.find((h: any) => h.action === 'feedback_sent' || h.action === 'rejected');
            const lastResubmit = propHistory.find((h: any) => h.action === 'resubmitted');
            const hasUnresolvedFeedback = !!lastFeedback && (
              !lastResubmit || new Date(lastFeedback.created_at) > new Date(lastResubmit.created_at)
            );
            const canResubmit = p.approval_status === 'rejected' || (p.approval_status === 'pending_review' && hasUnresolvedFeedback);
            const showHistory = expandedHistory[p.id];

            return (
              <Card key={p.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-lg bg-muted overflow-hidden shrink-0">
                      {img ? <img src={img} alt={title} className="h-full w-full object-cover" /> : <Home className="h-6 w-6 text-muted-foreground m-auto mt-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{title}</div>
                      <div className="text-sm text-muted-foreground">
                        {p.city}{p.sub_city ? `, ${p.sub_city}` : ''} • {p.property_type} • {p.listing_type}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.price?.toLocaleString()} {p.currency} • {p.bedrooms || 0} bed • {p.area_sqm || 0} sqm
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={statusColors[p.status] || statusColors.draft}>{p.status}</Badge>
                      {p.approval_status !== 'approved' && (
                        <span className="text-[10px] text-yellow-600 font-medium">
                          {p.approval_status === 'pending_review' ? 'Pending Approval' : p.approval_status === 'rejected' ? 'Rejected' : 'Draft'}
                        </span>
                      )}
                      {p.has_pending_changes && (
                        <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50 text-[10px]">
                          Edits pending QC
                        </Badge>
                      )}
                      <QualityScoreBadge score={p.listing_quality_score ?? 0} />
                      {p.approval_status === 'approved' && p.status === 'active' && (
                        <BoostListingButton listingId={p.id} listingType="property" />
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/dashboard/properties/${p.id}/edit`)}>
                          <Pencil className="h-4 w-4 mr-2" /> Edit Property
                        </DropdownMenuItem>
                        {isApproved && p.status !== 'active' && (
                          <DropdownMenuItem onClick={() => statusMutation.mutate({ id: p.id, status: 'active' })}>
                            <Eye className="h-4 w-4 mr-2" /> Make Active (Show on Site)
                          </DropdownMenuItem>
                        )}
                        {p.status === 'active' && (
                          <DropdownMenuItem onClick={() => statusMutation.mutate({ id: p.id, status: 'draft' })}>
                            <EyeOff className="h-4 w-4 mr-2" /> Set to Draft (Hide)
                          </DropdownMenuItem>
                        )}
                        {isApproved && p.listing_type === 'sale' && p.status !== 'sold' && (
                          <DropdownMenuItem onClick={() => markSoldMutation.mutate({ property: p, finalStatus: 'sold' })}>
                            <CheckCircle className="h-4 w-4 mr-2" /> Mark as Sold (close inquiries)
                          </DropdownMenuItem>
                        )}
                        {isApproved && p.listing_type === 'rent' && p.status !== 'rented' && (
                          <DropdownMenuItem onClick={() => markSoldMutation.mutate({ property: p, finalStatus: 'rented' })}>
                            <CheckCircle className="h-4 w-4 mr-2" /> Mark as Rented (close inquiries)
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(p)}>
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {(p.reviewer_notes || propHistory.length > 0) && (
                    <div className={`rounded-md border-l-4 p-3 text-sm ${
                      p.approval_status === 'rejected'
                        ? 'border-l-red-500 bg-red-50 text-red-900'
                        : p.approval_status === 'approved'
                          ? 'border-l-green-500 bg-green-50 text-green-900'
                          : 'border-l-yellow-500 bg-yellow-50 text-yellow-900'
                    }`}>
                      <div className="flex items-start gap-2">
                        <MessageSquare className="h-4 w-4 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="font-semibold text-xs uppercase tracking-wide">
                              {hasUnresolvedFeedback ? 'Action needed: QC feedback' : 'QC Review Feedback'}
                            </div>
                            {propHistory.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setExpandedHistory(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                                className="text-xs underline opacity-80 hover:opacity-100 flex items-center gap-1"
                              >
                                {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                {showHistory ? 'Hide' : 'Show'} history ({propHistory.length})
                              </button>
                            )}
                          </div>
                          {p.reviewer_notes && <p className="whitespace-pre-wrap">{p.reviewer_notes}</p>}

                          {showHistory && propHistory.length > 0 && (
                            <div className="mt-3 space-y-2 border-t border-current/20 pt-3">
                              {propHistory.map((h: any) => {
                                const cfg = historyActionConfig[h.action] || historyActionConfig.feedback_sent;
                                const Icon = cfg.icon;
                                return (
                                  <div key={h.id} className="flex gap-2 items-start">
                                    <div className={`h-2 w-2 rounded-full ${cfg.dot} mt-1.5 shrink-0`} />
                                    <div className="flex-1 text-xs">
                                      <div className="flex items-center gap-1.5 font-medium">
                                        <Icon className="h-3 w-3" />
                                        <span>{cfg.label}</span>
                                        <span className="opacity-60">• {new Date(h.created_at).toLocaleString()}</span>
                                      </div>
                                      {h.notes && <p className="opacity-90 whitespace-pre-wrap mt-0.5">{h.notes}</p>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {canResubmit && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/dashboard/properties/${p.id}/edit`)}
                                className="h-7 text-xs"
                              >
                                <Pencil className="h-3 w-3 mr-1" /> Edit & Resubmit
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => resubmitMutation.mutate(p)}
                                disabled={resubmitMutation.isPending}
                                className="h-7 text-xs"
                              >
                                <Send className="h-3 w-3 mr-1" /> Resubmit for Review
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20 text-muted-foreground">
          <p>You haven't listed any properties yet.</p>
        </div>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Property</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete "<strong>{deleteTarget && getName(deleteTarget.title)}</strong>"? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QualityScoreBadge({ score }: { score: number }) {
  let tone = 'bg-red-100 text-red-700 border-red-200';
  let label = 'Low quality';
  let Icon = AlertCircle;
  if (score >= 80) { tone = 'bg-emerald-100 text-emerald-700 border-emerald-200'; label = 'Excellent'; Icon = Sparkles; }
  else if (score >= 60) { tone = 'bg-blue-100 text-blue-700 border-blue-200'; label = 'Good'; Icon = Sparkles; }
  else if (score >= 40) { tone = 'bg-amber-100 text-amber-700 border-amber-200'; label = 'Fair'; Icon = AlertCircle; }

  const tip = score >= 80
    ? 'Eligible for boost / featured campaigns.'
    : score >= 60
      ? 'Add a few more photos or extend the description for top placement.'
      : score >= 40
        ? 'Add at least 5 photos and a 200+ char description to qualify for featured placement.'
        : 'Add photos, a detailed description, and key attributes (bedrooms, area, location) to improve visibility.';

  return (
    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium ${tone}`} title={tip}>
      <Icon className="h-3 w-3" />
      <span>Quality {score}/100</span>
      <span className="opacity-70">· {label}</span>
    </div>
  );
}
